import { api } from './api.js';

// Types (mirror server/src/routes/admin.ts serializers)
export type UserRole = 'jobseeker' | 'hr' | 'admin';
export type RequestStatus = 'unhandled' | 'all';

export interface AdminOverview {
  users: { total: number; jobseekers: number; hr: number; admins: number; disabled: number };
  jobs: { total: number; draft: number; published: number; closed: number };
  applications: { total: number; submitted: number; under_review: number; shortlisted: number; rejected: number };
  screenings: { queued: number; running: number; done: number; failed: number; manual_review: number };
  requests: { contactUnhandled: number; demoUnhandled: number };
  recentActivity: Array<{ id: string; action: string; targetType: string; targetId: string; actorName: string | null; createdAt: string }>;
}
export interface AdminUserRow { id: string; email: string; name: string; role: UserRole; disabledAt: string | null; emailVerifiedAt: string | null; createdAt: string }
export interface AdminUserDetail { user: AdminUserRow; profile: object | null; activity: { jobs: number; applications: number; resumes: number } }
// contact_submissions has no `subject` column — do not add one here.
export interface AdminContactRow { id: string; name: string; email: string; message: string; handledAt: string | null; createdAt: string }
// demo_requests has `company`, not `phone` — do not add a phone field here.
export interface AdminDemoRow { id: string; name: string; email: string; company: string; message: string; handledAt: string | null; createdAt: string }

export type CompanyStatus = 'active' | 'suspended';
export type CompanyRole = 'owner' | 'member';
export type CompanyMemberStatus = 'invited' | 'active' | 'suspended' | 'removed';

export interface AdminCompanyRow {
  id: string;
  slug: string;
  name: string;
  website: string;
  description: string;
  status: CompanyStatus;
  evidenceRetentionDays: number;
  createdAt: string;
  updatedAt: string;
}
// The list endpoint adds one derived field the detail endpoint does not carry.
export interface AdminCompanyListRow extends AdminCompanyRow { memberCount: number }
export interface AdminCompanyMemberRow {
  userId: string;
  email: string;
  name: string;
  companyRole: CompanyRole;
  status: CompanyMemberStatus;
  disabled: boolean;
  joinedAt: string | null;
  removedAt: string | null;
}
export interface AdminCompanyDetail {
  company: AdminCompanyRow;
  members: AdminCompanyMemberRow[];
  activity: { jobs: number; publishedJobs: number; applications: number };
}

function qs(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) usp.set(key, String(value));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export const adminOverview = () => api<AdminOverview>('/api/admin/overview');

export const adminListUsers = (params: { role?: UserRole; q?: string; page?: number } = {}) =>
  api<{ users: AdminUserRow[]; page: number; hasMore: boolean }>(`/api/admin/users${qs(params)}`);

export const adminGetUser = (id: string) => api<AdminUserDetail>(`/api/admin/users/${id}`);

export const adminDisableUser = (id: string) => api<{ ok: true }>(`/api/admin/users/${id}/disable`, { method: 'POST', body: {} });

export const adminEnableUser = (id: string) => api<{ ok: true }>(`/api/admin/users/${id}/enable`, { method: 'POST', body: {} });

export const adminDeleteUser = (id: string) => api<{ ok: true }>(`/api/admin/users/${id}`, { method: 'DELETE' });

export const adminListContact = (params: { status?: RequestStatus; page?: number } = {}) =>
  api<{ items: AdminContactRow[]; page: number; hasMore: boolean }>(`/api/admin/contact${qs(params)}`);

export const adminListDemo = (params: { status?: RequestStatus; page?: number } = {}) =>
  api<{ items: AdminDemoRow[]; page: number; hasMore: boolean }>(`/api/admin/demo${qs(params)}`);

export const adminMarkContact = (id: string, handled: boolean) => api<{ ok: true }>(`/api/admin/contact/${id}`, { method: 'PATCH', body: { handled } });

export const adminMarkDemo = (id: string, handled: boolean) => api<{ ok: true }>(`/api/admin/demo/${id}`, { method: 'PATCH', body: { handled } });

export const adminListCompanies = (params: { q?: string; page?: number } = {}) =>
  api<{ companies: AdminCompanyListRow[]; page: number; hasMore: boolean }>(`/api/admin/companies${qs(params)}`);

export const adminGetCompany = (id: string) => api<AdminCompanyDetail>(`/api/admin/companies/${id}`);

export const adminCreateCompany = (body: { name: string; website?: string; description?: string; slug?: string }) =>
  api<{ company: AdminCompanyRow }>('/api/admin/companies', { method: 'POST', body });

export const adminUpdateCompany = (
  id: string,
  body: Partial<{ name: string; slug: string; website: string; description: string; status: CompanyStatus }>,
) => api<{ company: AdminCompanyRow }>(`/api/admin/companies/${id}`, { method: 'PATCH', body });

export const adminAddCompanyMember = (id: string, body: { email: string; companyRole: CompanyRole }) =>
  api<{ member: AdminCompanyMemberRow }>(`/api/admin/companies/${id}/members`, { method: 'POST', body });

export const adminSetCompanyMemberRole = (id: string, userId: string, companyRole: CompanyRole) =>
  api<{ member: AdminCompanyMemberRow }>(`/api/admin/companies/${id}/members/${userId}`, {
    method: 'PATCH',
    body: { companyRole },
  });

export const adminRemoveCompanyMember = (id: string, userId: string) =>
  api<{ ok: true }>(`/api/admin/companies/${id}/members/${userId}`, { method: 'DELETE' });
